import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline'
import classNames from 'classnames'
import { OpenInWindow } from 'iconoir-react'
import { Info } from '../Info'
import { Tooltip } from '../Tooltip'
import TrendLabel from '../viz/TrendLabel'
import { useIsMobile } from 'app/hooks/useMediaQuery'
import { StatsCardType } from 'types/StatsCardType'

export function StatsCards({ title, stats }: { title?: string; stats: StatsCardType[] }) {
  // Mobile drops the comparison deltas to cut visual noise — just the numbers.
  const isMobile = useIsMobile()
  return (
    <>
      {title && <p className="text-ui-lg text-strong">{title}</p>}
      {/* Two-column compact grid on mobile so four KPIs read as a 2x2 block
          instead of four stacked rows; the original flex-wrap cluster returns
          at md. */}
      <div className="my-6 grid grid-cols-2 gap-3 md:my-8 md:flex md:flex-wrap md:justify-between md:gap-4">
        {stats?.map((card) => (
          <div
            key={card.label}
            className={classNames(
              `group flex min-w-0 flex-col gap-2 rounded-lg border border-default py-4 md:min-w-[220px] md:flex-1 md:gap-4 md:py-6`,
              { 'cursor-pointer hover:bg-surface-muted': card.onOpenClick },
            )}
            onClick={card.onOpenClick}
          >
            <div className="flex items-center justify-between px-4 md:px-6">
              <div className="flex items-center space-x-2">
                <p className="text-body-xs text-default md:text-body-sm">{card.label}</p>
                {card.infoTooltip ? <Info title={card.infoTooltip.title} message={card.infoTooltip.message} /> : null}
              </div>
              {card.onOpenClick ? (
                <OpenInWindow height={16} width={16} className="hidden cursor-pointer group-hover:block" />
              ) : null}
            </div>
            <div className="flex items-center gap-2 px-4 md:gap-4 md:px-6">
              {!card.noValue ? (
                <Tooltip
                  key={card.label}
                  className="cursor-default"
                  message={
                    card.tooltipData ? (
                      <div className="flex w-64 flex-col gap-2 p-1">
                        {card.tooltipData?.map((data) => (
                          <div key={data.label} className="flex items-center justify-between gap-2">
                            <p className="text-body-sm inverse-text-default">{data.label}:</p>
                            <p className="text-ui-sm inverse-text-strong">{data.value}</p>
                          </div>
                        ))}
                      </div>
                    ) : null
                  }
                >
                  <h4 className="text-strong">{card.value}</h4>
                </Tooltip>
              ) : (
                <div className="flex items-center gap-x-2 text-subtle">
                  <span className="text-body-sm">{card.noValue}</span>
                  <Tooltip message="Filtering by Ad Platform and Ad Campaign will enable this card.">
                    <QuestionMarkCircleIcon width={20} height={20} />
                  </Tooltip>
                </div>
              )}
              {!isMobile && card.trend ? (
                <TrendLabel previous={card.trend.previous} current={card.trend.current} />
              ) : null}
            </div>
            {card.bottomStatLabel && (
              <div className="-mb-3 flex justify-between border-t px-4 pt-3 md:px-6">
                <p className="text-body-xs text-default">{card.bottomStatLabel}</p>
                <p className="text-ui-sm text-strong">{card.bottomStatValue}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  )
}
